import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import IdeaDetail from '@/components/Ideas/IdeaDetail';
import { useTheme } from '@/context/ThemeContext';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { useAuthStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import useAppActive from '@/hooks/useAppActive';
import useStarterPrompts from '@/hooks/useStarterPrompts';
import { useWorkModeDisabled } from '@/lib/workModeAvailability';
import { openIdeaComposer } from '@/lib/openIdeaComposer';
import { IDEAS, IDEA_CATEGORIES, personalizedIdeas } from '@/constants/ideas';

function IdeaCard({ idea, palette, width, onPress }) {
  return (
    <Pressable accessibilityRole="button" accessibilityLabel={`${idea.title}, ${idea.mode === 'work' ? 'Work' : 'Chat'}`}
      onPress={() => onPress(idea)} style={({ hovered, pressed }) => [styles.card, { width, backgroundColor: hovered || pressed ? palette.hover : palette.surface, borderColor: palette.border }]}>
      <View style={styles.cardTop}>
        <Icon name={idea.icon || 'lightbulb'} size={27} color={palette.ink} />
        <Text size="xs" color="secondary">{idea.mode === 'work' ? 'Work' : 'Chat'}</Text>
      </View>
      <View style={styles.cardBottom}>
        <Text weight="medium" style={styles.cardTitle}>{idea.title}</Text>
        <View style={[styles.plus, { backgroundColor: palette.control }]}><Icon name="plus" size={12} color={palette.icon} /></View>
      </View>
    </Pressable>
  );
}

export default function IdeasPage() {
  const router = useRouter();
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const focused = useIsFocused();
  const active = useAppActive();
  const workDisabled = useWorkModeDisabled();
  const userId = useAuthStore(s => s.user?.id);
  const memoryEnabled = useAuthStore(s => s.user?.preferences?.memory?.isEnabled !== false);
  const [mode, setMode] = useState('chat');
  const [selected, setSelected] = useState(null);
  const [contentWidth, setContentWidth] = useState(0);
  // Ideas are account-wide, independent of the selected Work project or its memory toggle.
  const chat = useStarterPrompts({ mode: 'chat', project: null, enabled: focused && active, allowStale: false });
  const work = useStarterPrompts({ mode: 'work', project: null, enabled: focused && active && !workDisabled, allowStale: false });
  const personal = useMemo(() => personalizedIdeas(chat.items, work.items), [chat.items, work.items]);
  const filter = item => mode === 'all' || item.mode === mode;
  const forYou = personal.filter(filter).slice(0, 3);
  const groups = IDEA_CATEGORIES.map(category => ({ ...category, ideas: IDEAS.filter(item => item.category === category.id && filter(item)) })).filter(category => category.ideas.length);
  const isPending = chat.isPending || work.isPending;
  const columns = contentWidth >= 850 ? 3 : contentWidth >= 510 ? 2 : 1;
  const cardWidth = contentWidth ? (contentWidth - (columns - 1) * 14) / columns : '100%';
  // Resolve personal selections against the current account/revision, never an old modal snapshot.
  const selectedIdea = selected?.personalized
    ? userId === selected.userId && memoryEnabled && personal.find(item => item.id === selected.id && item.prompt === selected.prompt)
    : selected;
  const select = idea => setSelected({ ...idea, userId });
  const tryIdea = (idea, prompt) => {
    if (openIdeaComposer(router, idea, prompt)) setSelected(null);
  };
  const cards = items => <View style={styles.grid}>{items.map(idea => <IdeaCard key={idea.id} idea={idea} palette={palette} width={cardWidth} onPress={select} />)}</View>;

  return (
    <View style={[styles.page, { backgroundColor: colors.solid_color_primary }]}>
      <MetaHead title="Ideas" description="Discover what you can do with Gab AI Chat and Work." noindex />
      <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingBottom: insets.bottom + 72 }}>
        <View style={[styles.content, { paddingHorizontal: isXS ? 20 : 36 }]}>
          <View style={[styles.hero, { paddingTop: isXS ? 30 : 58 }]}>
            <View style={[styles.heroIcon, { backgroundColor: palette.preview }]}><Icon name="lightbulb" size={25} color={palette.ink} /></View>
            <Text tag="h1" accessibilityRole="header" weight="semibold" style={[styles.headline, { fontSize: isXS ? 34 : 44, lineHeight: isXS ? 40 : 52 }]}>What will you do next?</Text>
            <Text color="secondary" style={styles.subtitle}>A little inspiration for Chat. A head start for Work.</Text>
          </View>

          <View style={styles.toolbar}>
            <View style={[styles.segmented, { backgroundColor: palette.preview }]}>
              {[['all', 'All ideas'], ['chat', 'Chat'], ['work', 'Work']].map(([value, label]) => <Pressable key={value}
                accessibilityRole="button" accessibilityLabel={`Show ${label.toLowerCase()}`} accessibilityState={{ selected: mode === value }}
                onPress={() => setMode(value)} style={[styles.segment, { backgroundColor: mode === value ? palette.control : 'transparent' }]}>
                <Text size="s" numberOfLines={1} weight={mode === value ? 'semibold' : 'regular'} color={mode === value ? 'primary' : 'secondary'}>{label}</Text>
              </Pressable>)}
            </View>
          </View>
          {mode !== 'all' && <Text size="s" color="secondary" style={styles.modeHint}>{mode === 'chat' ? 'Think it through, learn something, or find the words.' : 'Create files, build projects, and get things done.'}</Text>}

          <View onLayout={event => setContentWidth(event.nativeEvent.layout.width)}>
            {(forYou.length > 0 || isPending) && <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionHeading}><Text tag="h2" accessibilityRole="header" weight="semibold" style={styles.sectionTitle}>For you</Text>
                  <Text size="xs" color="secondary">Inspired by your memory</Text></View>
                <Pressable accessibilityRole="link" accessibilityLabel="Manage memory" onPress={() => router.push('/memory')} style={styles.memoryLink}><Icon name="brain" size={18} color={palette.icon} /></Pressable>
              </View>
              {forYou.length ? cards(forYou) : <View accessibilityRole="progressbar" accessibilityLabel="Finding ideas for you" style={[styles.loading, { backgroundColor: palette.surface }]}>
                <ActivityIndicator size="small" color={palette.icon} /><Text size="s" color="secondary">Finding a little inspiration for you…</Text>
              </View>}
            </View>}
            {groups.map(category => <View key={category.id} style={styles.section}>
              <Text tag="h2" accessibilityRole="header" weight="semibold" style={[styles.sectionTitle, { marginBottom: 16 }]}>{category.title}</Text>
              {cards(category.ideas)}
            </View>)}
          </View>
          {!personal.length && !isPending && <Pressable accessibilityRole="link" onPress={() => router.push(userId ? '/memory' : '/login')}
            style={[styles.memoryNote, { borderColor: palette.border }]}>
            <Icon name="brain" size={18} color={palette.icon} /><Text size="s" color="secondary" style={{ flex: 1 }}>{!userId ? 'Sign in for ideas inspired by you.' : !memoryEnabled ? 'Turn on Memory for ideas inspired by you.' : 'Ideas get more personal with Memory.'}</Text><Icon name="arrow-right" size={14} color={palette.icon} />
          </Pressable>}
        </View>
      </ScrollView>
      {!!selectedIdea && <IdeaDetail key={`${selectedIdea.id}:${selectedIdea.prompt}`} idea={selectedIdea} onClose={() => setSelected(null)} onTry={tryIdea} />}
    </View>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1 },
  content: { width: '100%', maxWidth: 1080, alignSelf: 'center' },
  hero: { paddingBottom: 34, alignItems: 'center' },
  heroIcon: { height: 54, width: 54, borderRadius: 17, alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  headline: { letterSpacing: -1.5, textAlign: 'center' },
  subtitle: { fontSize: 16, lineHeight: 24, textAlign: 'center', marginTop: 12, maxWidth: 390 },
  toolbar: { alignItems: 'center', marginBottom: 8 },
  segmented: { flexDirection: 'row', padding: 4, borderRadius: 12 },
  segment: { flexGrow: 1, minWidth: 88, minHeight: 36, paddingHorizontal: 14, alignItems: 'center', justifyContent: 'center', borderRadius: 9 },
  modeHint: { marginTop: 10, textAlign: 'center' },
  section: { marginTop: 32 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  sectionHeading: { gap: 5 },
  sectionTitle: { fontSize: 21, lineHeight: 28, letterSpacing: -0.4 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  card: { minHeight: 166, borderWidth: 1, borderRadius: 20, padding: 22, justifyContent: 'space-between', gap: 26 },
  cardTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  cardBottom: { flexDirection: 'row', alignItems: 'flex-end', gap: 12 },
  cardTitle: { flex: 1, fontSize: 19, lineHeight: 25, letterSpacing: -0.3 },
  plus: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  memoryLink: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center', borderRadius: 12 },
  loading: { minHeight: 166, borderRadius: 20, alignItems: 'center', justifyContent: 'center', gap: 12 },
  memoryNote: { borderTopWidth: 1, marginTop: 44, paddingTop: 22, flexDirection: 'row', alignItems: 'center', gap: 12 },
});
